// Copyright (c) 2025 Audere, All Rights Reserved

import {
  ListItem,
  IconButton,
  ListItemIcon,
  Box,
  ListItemText,
  Typography,
  Tooltip,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import type { TestListsItemProps } from "../types/projects";
import { theme } from "../../theme";
import { createStyleSheet } from "../utils/styles";
import labsUrl from "../../assets/labs.svg";
import { useCallback } from "react";
import { getRDTName } from "../utils/string";

dayjs.extend(relativeTime);

const TestListsItem = (props: TestListsItemProps) => {
  const { test, isSelected, onSelectTest, isCollapsed } = props;

  const handleSelectTest = useCallback(() => {
    onSelectTest(test);
  }, [onSelectTest, test]);

  return (
    <Tooltip title={isCollapsed && getRDTName(test)}>
      <ListItem
        sx={{
          ...styles.listItemContainer,
          py: isCollapsed ? 2 : 1.5,
          background: isSelected ? theme.palette.detailBackground.main : "transparent",
          borderRight: isSelected ? `4px solid ${theme.palette.primary.main}` : "none",
          "& .MuiListItemIcon-root": {
            ...(isCollapsed && {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }),
          },
        }}
        secondaryAction={
          !isCollapsed && (
            <IconButton size="small">
              <ChevronRightIcon color="primary" />
            </IconButton>
          )
        }
        onClick={handleSelectTest}
      >
        <ListItemIcon sx={styles.listItemIcon}>
          <Box component="img" src={labsUrl} sx={styles.newTestLabsImg} alt="New test labs" />
        </ListItemIcon>
        {!isCollapsed && (
          <ListItemText
            primary={
              <Typography variant="body1bold" color="primary">
                {getRDTName(test)}
              </Typography>
            }
            secondary={
              <Typography variant="body2" color="text.secondary">
                {dayjs(test.last_modified).fromNow()}
              </Typography>
            }
          />
        )}
      </ListItem>
    </Tooltip>
  );
};

export default TestListsItem;

const styles = createStyleSheet({
  listItemContainer: {
    px: 2,
  },
  listItemIcon: { minWidth: 36 },
  newTestLabsImg: { height: theme.spacing(3), objectFit: "contain" },
});
